import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, SignOut, Buildings, CalendarBlank, CurrencyDollar, MapPin, Bell, Image as ImageIcon, HardHat, ArrowsLeftRight, FileText, X, Trash, FolderOpen, Trophy, CheckCircle, Spinner, Gavel, CaretDown, House, Prohibit } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import LocationSearch from '../components/LocationSearch';
import BidManagement from '../components/BidManagement';
import HelpButton from '../components/HelpButton';
import AdminApproval from '../components/AdminApproval';
import AdminNav from '../components/AdminNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthImage = ({ fileRef, alt, className }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!fileRef) return;
    const fileId = fileRef.includes('/') ? fileRef.split('/').pop().split('.')[0] : fileRef;
    let url = null;
    axios.get(`${API}/files/${fileId}/download`, { responseType: 'blob' })
      .then(res => { url = window.URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [fileRef]);
  if (!src) return <div className={className} style={{ background: 'var(--bg-elevated)' }}><Buildings size={48} weight="duotone" style={{ color: 'var(--text-muted)', margin: 'auto' }} /></div>;
  return <img src={src} alt={alt} className={className} />;
};

const OwnerDashboard = () => {
  const { user, logout, adminView, setAdminView } = useContext(AuthContext);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '', description: '', specs: '', location: '', budget_range: '', deadline: '', property_type: 'multifamily'
  });
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [projectDocs, setProjectDocs] = useState([]);
  const [creatingProject, setCreatingProject] = useState(false);
  const [activeFilter, setActiveFilter] = useState('new');
  const [draggedProject, setDraggedProject] = useState(null);
  const [dragOverFilter, setDragOverFilter] = useState(null);
  const [nearbyContractors, setNearbyContractors] = useState([]);
  const [showContractorSearch, setShowContractorSearch] = useState(false);
  const [searchingContractors, setSearchingContractors] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [properties, setProperties] = useState([]);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [activeProperty, setActiveProperty] = useState(null);
  const [dragOverProperty, setDragOverProperty] = useState(null);
  const [ownerBids, setOwnerBids] = useState([]);
  const [trashedBids, setTrashedBids] = useState([]);
  const [trashedProjects, setTrashedProjects] = useState([]);
  const [showTrash, setShowTrash] = useState(false);

  useEffect(() => { fetchData(); fetchProperties(); fetchOwnerBids(); fetchTrashedBids(); fetchTrashedProjects(); }, []);

  // Poll notifications every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/notifications`);
        setNotifications(res.data);
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/projects`), axios.get(`${API}/notifications`)
      ]);
      setProjects(projectsRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.patch(`${API}/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    await Promise.all(unread.map(n => axios.patch(`${API}/notifications/${n.id}/read`).catch(() => {})));
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const fetchProperties = async () => {
    try { const res = await axios.get(`${API}/properties`); setProperties(res.data); } catch { /* ignore */ }
  };

  const fetchOwnerBids = async () => {
    try { const res = await axios.get(`${API}/owner/bids`); setOwnerBids(res.data); } catch { /* ignore */ }
  };

  const fetchTrashedBids = async () => {
    try { const res = await axios.get(`${API}/owner/bids/trash`); setTrashedBids(res.data); } catch { /* ignore */ }
  };

  const handleRestoreBid = async (bidId) => {
    try {
      await axios.post(`${API}/bids/${bidId}/restore`);
      toast.success('Bid restored');
      fetchOwnerBids();
      fetchTrashedBids();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to restore'); }
  };

  const handlePermanentDeleteBid = async (bidId) => {
    if (!window.confirm('Permanently delete this bid? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/bids/${bidId}`);
      toast.success('Bid permanently deleted');
      fetchTrashedBids();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const fetchTrashedProjects = async () => {
    try {
      const res = await axios.get(`${API}/owner/projects/trash`);
      setTrashedProjects(res.data);
    } catch { setTrashedProjects([]); }
  };

  const handleTrashProject = async (e, projectId, projectTitle) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/projects/${projectId}/trash`);
      toast.success(`"${projectTitle}" moved to Trash`);
      fetchData();
      fetchTrashedProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to trash project'); }
  };

  const handleRestoreProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/restore`);
      toast.success('Project restored');
      fetchData();
      fetchTrashedProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to restore'); }
  };

  const handlePermanentDeleteProject = async (projectId, projectTitle) => {
    if (!window.confirm(`Permanently delete "${projectTitle}"? This will remove all bids, contracts, tasks and documents. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project permanently deleted');
      fetchTrashedProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleAddProperty = async () => {
    if (!newPropertyName.trim()) return;
    try {
      await axios.post(`${API}/properties`, { name: newPropertyName.trim() });
      toast.success('Property added');
      setNewPropertyName('');
      setShowAddProperty(false);
      fetchProperties();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteProperty = async (e, propId, propName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete property "${propName}"?`)) return;
    try { await axios.delete(`${API}/properties/${propId}`); toast.success('Property deleted'); fetchProperties(); if (activeProperty === propId) setActiveProperty(null); }
    catch { toast.error('Failed to delete'); }
  };

  const handleBidDropOnProperty = async (e, propertyId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverProperty(null);
    const bidId = e.dataTransfer.getData('bid-id');
    if (!bidId) return;
    try {
      await axios.post(`${API}/properties/${propertyId}/bids/${bidId}`);
      const propName = properties.find(p => p.id === propertyId)?.name || 'property';
      toast.success(`Bid moved to ${propName}`);
      fetchProperties();
      fetchOwnerBids();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const onDrop = (acceptedFiles) => { setProjectPhotos([...projectPhotos, ...acceptedFiles]); };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'image/*': [] }, multiple: true });

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreatingProject(true);
    try {
      const response = await axios.post(`${API}/projects`, newProject);
      const projectId = response.data.id;
      if (projectPhotos.length > 0) {
        for (const photo of projectPhotos) {
          const formData = new FormData();
          formData.append('file', photo);
          await axios.post(`${API}/projects/${projectId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      if (projectDocs.length > 0) {
        for (const doc of projectDocs) {
          const formData = new FormData();
          formData.append('file', doc);
          await axios.post(`${API}/projects/${projectId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      toast.success('Project created successfully!');
      setShowCreateDialog(false);
      setNewProject({ title: '', description: '', specs: '', location: '', budget_range: '', deadline: '', property_type: 'multifamily' });
      setProjectPhotos([]);
      setProjectDocs([]);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create project'); }
    finally { setCreatingProject(false); }
  };

  const getStatusColor = (status) => `hb-badge hb-badge-${status}`;

  const handleDeleteProject = async (e, projectId, projectTitle) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${projectTitle}"?\n\nThis will permanently remove the project and all associated bids, contracts, tasks, and documents.`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete project'); }
  };

  const SIDEBAR_FILTERS = [
    { id: 'new', label: 'New Projects', icon: FolderOpen, statuses: ['open', 'draft', 'bidding'], dropStatus: 'open' },
    { id: 'awarded', label: 'Awarded', icon: Trophy, statuses: ['awarded'], dropStatus: 'awarded' },
    { id: 'in_progress', label: 'In Progress', icon: Spinner, statuses: ['in_progress'], dropStatus: 'in_progress' },
    { id: 'denied', label: 'Denied', icon: Prohibit, statuses: [], computed: true },
    { id: 'closed', label: 'Closed', icon: CheckCircle, statuses: ['completed', 'cancelled'], dropStatus: 'completed' },
  ];

  const handleDragStart = (e, project) => {
    setDraggedProject(project);
    e.dataTransfer.setData('project-id', project.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, filterId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFilter(filterId);
  };

  const handleDragEnter = (e, filterId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFilter(filterId);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverFilter(null);
  };

  const handleDrop = async (e, filterId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFilter(null);
    // Resolve project from state OR dataTransfer fallback
    let project = draggedProject;
    if (!project) {
      const projectId = e.dataTransfer.getData('project-id');
      if (projectId) project = projects.find(p => p.id === projectId);
    }
    if (!project) return;
    const filter = SIDEBAR_FILTERS.find(f => f.id === filterId);
    if (!filter || filter.computed) { setDraggedProject(null); return; }
    // Skip if already in this section
    if (filter.statuses.includes(project.status)) { setDraggedProject(null); return; }
    const newStatus = filter.dropStatus;
    // Optimistic UI update
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, status: newStatus } : p));
    setDraggedProject(null);
    try {
      await axios.patch(`${API}/projects/${project.id}/status`, { status: newStatus });
      toast.success(`Moved to ${filter.label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
      fetchData(); // Revert on failure
    }
  };

  // Compute denied projects: projects where all bids were rejected and at least 1 bid exists
  const getDeniedProjectIds = () => {
    const bidsByProject = {};
    ownerBids.forEach(b => {
      if (!bidsByProject[b.project_id]) bidsByProject[b.project_id] = [];
      bidsByProject[b.project_id].push(b);
    });
    const denied = new Set();
    Object.entries(bidsByProject).forEach(([projId, bids]) => {
      if (bids.length > 0 && bids.every(b => b.status === 'rejected')) {
        // Only count if the project is not already awarded/in_progress/completed
        const proj = projects.find(p => p.id === projId);
        if (proj && !['awarded', 'in_progress', 'completed'].includes(proj.status)) {
          denied.add(projId);
        }
      }
    });
    return denied;
  };

  const deniedProjectIds = getDeniedProjectIds();

  const filteredProjects = projects.filter(p => {
    const filter = SIDEBAR_FILTERS.find(f => f.id === activeFilter);
    if (!filter) return true;
    if (filter.id === 'denied') return deniedProjectIds.has(p.id);
    return filter.statuses.includes(p.status);
  });

  const getFilterCount = (filterId) => {
    const filter = SIDEBAR_FILTERS.find(f => f.id === filterId);
    if (!filter) return 0;
    if (filter.id === 'denied') return deniedProjectIds.size;
    return projects.filter(p => filter.statuses.includes(p.status)).length;
  };

  const handleContractorSearch = async ({ location, radius }) => {
    if (!location) { setNearbyContractors([]); setShowContractorSearch(false); return; }
    setSearchingContractors(true);
    setShowContractorSearch(true);
    try {
      const res = await axios.get(`${API}/search/contractors`, { params: { location, radius } });
      setNearbyContractors(res.data);
      if (res.data.length === 0) toast.info('No contractors found in this area');
    } catch (err) { toast.error('Search failed'); }
    finally { setSearchingContractors(false); }
  };

  if (loading) {
    return <div className="hb-bg min-h-screen flex items-center justify-center">
      <img src="/harperbids-logo.png" alt="Loading" className="h-16 opacity-50 animate-pulse hb-logo-glow" />
    </div>;
  }

  return (
    <div className="hb-bg"><div style={{ width: '100%' }} className="min-h-screen">
      {/* Header */}
      <header className="hb-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/harperbids-logo.png" alt="HarperBids" className="h-10 hb-logo-glow" />
            <div>
              <h1 className="text-lg font-bold font-heading" style={{ color: 'var(--gold-light)' }}>HARPERBIDS</h1>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.role === 'admin' ? 'Master Admin Dashboard' : 'Property Owner Dashboard'}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="nav-all-jobs"
              onClick={() => navigate('/jobs')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.15)', color: 'var(--gold)' }}
            >
              All Jobs
            </button>
            <button
              data-testid="nav-contractors"
              onClick={() => navigate('/contractors')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.15)', color: 'var(--gold)' }}
            >
              Contractors
            </button>
            {user.role === 'admin' && (
              <AdminNav currentView="owner" />
            )}
            <HelpButton />
            <div className="relative">
              <button data-testid="notifications-button" onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 rounded-lg btn-ghost">
                <Bell size={24} weight="duotone" style={{ color: 'var(--text-secondary)' }} />
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full" style={{ background: '#ef4444', color: '#fff' }}>
                    {notifications.filter(n => !n.read).length}
                  </span>
                )}
              </button>
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                  <div data-testid="notifications-dropdown" className="absolute right-0 top-12 z-50 w-96 rounded-xl overflow-hidden" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <h3 className="text-sm font-bold font-heading" style={{ color: 'var(--gold-light)' }}>Notifications</h3>
                      {notifications.filter(n => !n.read).length > 0 && (
                        <button data-testid="mark-all-read" onClick={markAllRead} className="text-xs font-semibold" style={{ color: 'var(--gold)' }}>Mark all read</button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>No notifications</div>
                      ) : (
                        notifications.slice(0, 20).map(n => (
                          <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => { markAsRead(n.id); setShowNotifications(false); }}
                            className="w-full text-left px-4 py-3 transition-colors block"
                            style={{ background: n.read ? 'transparent' : 'rgba(201,206,211,0.05)', borderBottom: '1px solid var(--border-subtle)' }}>
                            <div className="flex items-start gap-2">
                              {!n.read && <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: 'var(--gold)' }} />}
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold mb-0.5" style={{ color: n.read ? 'var(--text-muted)' : 'var(--text-primary)' }}>{n.title}</p>
                                <p className="text-xs line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{n.message}</p>
                                <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3 pl-4" style={{ borderLeft: '1px solid var(--border-primary)' }}>
              <div className="text-right">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.company || user.email}</p>
              </div>
              <Button data-testid="logout-button" onClick={logout} variant="ghost" size="sm" className="btn-ghost">
                <SignOut size={18} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Body: Sidebar + Content */}
      <div className="flex" style={{ minHeight: 'calc(100vh - 73px)' }}>
        {/* Sidebar */}
        <aside className="w-64 shrink-0 p-6" style={{ background: 'rgba(12,12,18,0.7)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
          <h3 className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: 'var(--text-muted)' }}>Projects</h3>
          {SIDEBAR_FILTERS.map(filter => {
            const Icon = filter.icon;
            const isActive = activeFilter === filter.id;
            const isDragOver = dragOverFilter === filter.id;
            const count = getFilterCount(filter.id);
            return (
              <div
                key={filter.id}
                data-testid={`owner-filter-${filter.id}`}
                onClick={() => { setActiveFilter(filter.id); setActiveProperty(null); }}
                onDragEnter={(e) => handleDragEnter(e, filter.id)}
                onDragOver={(e) => handleDragOver(e, filter.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, filter.id)}
                className="flex items-center gap-3 px-4 py-4 rounded-2xl mb-2 cursor-pointer transition-all"
                style={{
                  background: isDragOver ? 'rgba(201,206,211,0.2)' : isActive ? 'rgba(201,206,211,0.1)' : 'transparent',
                  color: isActive ? 'var(--gold)' : 'var(--text-primary)',
                  border: isDragOver ? '1px dashed var(--gold)' : '1px solid transparent',
                }}
              >
                <Icon size={24} weight={isActive ? 'fill' : 'duotone'} />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{filter.label}</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {count} {count === 1 ? 'project' : 'projects'}
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Properties ── */}
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between px-4 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Properties</span>
              <button data-testid="add-property-btn" onClick={() => setShowAddProperty(!showAddProperty)} className="p-1 rounded transition-all" style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--gold)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}>
                <Plus size={16} weight="bold" />
              </button>
            </div>
            {showAddProperty && (
              <div className="px-4 mb-3">
                <input data-testid="new-property-input" value={newPropertyName} onChange={(e) => setNewPropertyName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddProperty(); }}
                  placeholder="Property name..."
                  className="hb-input w-full text-xs px-3 py-2 mb-2 rounded-lg"
                  autoFocus />
                <div className="flex gap-2">
                  <Button data-testid="save-property-btn" onClick={handleAddProperty} disabled={!newPropertyName.trim()} size="sm" className="btn-gold text-xs flex-1">Add</Button>
                  <Button onClick={() => { setShowAddProperty(false); setNewPropertyName(''); }} size="sm" className="btn-ghost text-xs flex-1" style={{ border: '1px solid var(--border-secondary)' }}>Cancel</Button>
                </div>
              </div>
            )}
            {properties.map(prop => {
              const isActive = activeProperty === prop.id;
              const isDragOver = dragOverProperty === prop.id;
              const bidCount = prop.bid_ids?.length || 0;
              return (
                <div key={prop.id} data-testid={`property-${prop.id}`}
                  onClick={() => { setActiveProperty(isActive ? null : prop.id); setActiveFilter(null); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverProperty(prop.id); }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverProperty(prop.id); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragOverProperty(null); }}
                  onDrop={(e) => handleBidDropOnProperty(e, prop.id)}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl mb-1 cursor-pointer transition-all"
                  style={{
                    background: isDragOver ? 'rgba(201,206,211,0.2)' : isActive ? 'rgba(201,206,211,0.1)' : 'transparent',
                    color: isActive ? 'var(--gold)' : 'var(--text-primary)',
                    border: isDragOver ? '1px dashed var(--gold)' : '1px solid transparent',
                  }}>
                  <House size={20} weight={isActive ? 'fill' : 'duotone'} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{prop.name}</div>
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{bidCount} bid{bidCount !== 1 ? 's' : ''}</div>
                  </div>
                  <button data-testid={`delete-property-${prop.id}`} onClick={(e) => handleDeleteProperty(e, prop.id, prop.name)} className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--text-muted)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.opacity = '0.5'; }}>
                    <Trash size={14} />
                  </button>
                </div>
              );
            })}
            {properties.length === 0 && !showAddProperty && (
              <p className="px-4 text-xs" style={{ color: 'var(--text-muted)' }}>No properties yet</p>
            )}
          </div>

          {/* ── Bid Trash ── */}
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <div
              data-testid="owner-bid-trash"
              onClick={() => { setShowTrash(!showTrash); setActiveFilter(null); setActiveProperty(null); }}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer transition-all"
              style={{
                background: showTrash ? 'rgba(239,68,68,0.1)' : 'transparent',
                color: showTrash ? '#f87171' : 'var(--text-muted)',
                border: showTrash ? '1px solid rgba(239,68,68,0.2)' : '1px solid transparent',
              }}
            >
              <Trash size={20} weight={showTrash ? 'fill' : 'duotone'} />
              <div className="flex-1">
                <div className="font-semibold text-sm">Trash</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{trashedBids.length + trashedProjects.length} item{trashedBids.length + trashedProjects.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="hb-stat">
            <div className="text-sm font-medium uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Total Projects</div>
            <div className="text-3xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>{projects.length}</div>
          </div>
          <div className="hb-stat">
            <div className="text-sm font-medium uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Active Bids</div>
            <div className="text-3xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>
              {projects.reduce((acc, p) => acc + (p.bid_count || 0), 0)}
            </div>
          </div>
          <div className="hb-stat">
            <div className="text-sm font-medium uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Awarded</div>
            <div className="text-3xl font-bold font-heading" style={{ color: '#fbbf24' }}>
              {projects.filter(p => p.status === 'awarded').length}
            </div>
          </div>
        </div>

        {/* Location Search Bar */}
        <div className="mb-6" data-testid="owner-location-search">
          <LocationSearch
            onSearch={handleContractorSearch}
            userLocation={user.location || ''}
            placeholder="Search contractors near a city, address, or ZIP..."
          />
        </div>

        {/* Nearby Contractors Results */}
        {showContractorSearch && (
          <div className="mb-8" data-testid="contractor-search-results">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>
                Nearby Contractors {nearbyContractors.length > 0 && `(${nearbyContractors.length})`}
              </h2>
              <button data-testid="close-contractor-results" onClick={() => setShowContractorSearch(false)} className="text-sm px-3 py-1 rounded-lg" style={{ color: 'var(--text-muted)', background: 'var(--bg-elevated)' }}>
                Close
              </button>
            </div>
            {searchingContractors ? (
              <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Searching...</div>
            ) : nearbyContractors.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {nearbyContractors.map((gc) => (
                  <div key={gc.id} data-testid={`contractor-card-${gc.id}`} className="hb-card p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>{gc.company || gc.name}</h3>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{gc.name}</p>
                      </div>
                      {gc.distance_miles !== null && (
                        <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: 'rgba(201,206,211,0.1)', color: 'var(--gold)' }}>
                          {gc.distance_miles} mi
                        </span>
                      )}
                    </div>
                    {gc.location && <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}><MapPin size={12} weight="fill" className="inline mr-1" />{gc.location}</p>}
                    {gc.phone && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{gc.phone}</p>}
                    {gc.verified && <span className="text-xs mt-2 inline-block px-2 py-0.5 rounded-full" style={{ background: 'rgba(74,222,128,0.1)', color: '#4ade80' }}>{gc.verified}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="hb-card p-8 text-center">
                <p style={{ color: 'var(--text-muted)' }}>No contractors found in this area</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ PROPERTY VIEW (when property selected) ═══ */}
        {showTrash ? (
          <div data-testid="trash-view">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Trash size={24} weight="duotone" style={{ color: '#f87171' }} />
                <div>
                  <h2 className="text-2xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>Trash</h2>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{trashedProjects.length} project{trashedProjects.length !== 1 ? 's' : ''}, {trashedBids.length} bid{trashedBids.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <Button onClick={() => { setShowTrash(false); setActiveFilter('new'); }} size="sm" className="btn-ghost text-xs" style={{ border: '1px solid var(--border-secondary)' }}>
                <X size={14} className="mr-1" /> Close
              </Button>
            </div>

            {/* Trashed Projects */}
            {trashedProjects.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>Projects ({trashedProjects.length})</h3>
                <div className="space-y-3">
                  {trashedProjects.map(project => (
                    <div key={project.id} data-testid={`trash-project-${project.id.slice(0,8)}`} className="hb-card p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold font-heading mb-1" style={{ color: 'var(--text-primary)' }}>{project.title}</h4>
                          <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                            <span className="flex items-center gap-1"><MapPin size={12} /> {project.location}</span>
                            <span className="flex items-center gap-1"><CurrencyDollar size={12} /> {project.budget_range}</span>
                            <span>{project.bid_count} bid{project.bid_count !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button data-testid={`restore-project-${project.id.slice(0,8)}`} onClick={() => handleRestoreProject(project.id)} size="sm" className="text-xs gap-1" style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.25)' }}>
                            Restore
                          </Button>
                          <Button data-testid={`perm-delete-project-${project.id.slice(0,8)}`} onClick={() => handlePermanentDeleteProject(project.id, project.title)} size="sm" className="text-xs gap-1" style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', border: '1px solid rgba(239,68,68,0.15)' }}>
                            <Trash size={14} weight="bold" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trashed Bids */}
            {trashedBids.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>Bids ({trashedBids.length})</h3>
                <div className="space-y-3">
                  {trashedBids.map(bid => (
                    <div key={bid.id} data-testid={`trash-bid-${bid.id.slice(0,8)}`} className="hb-card p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <HardHat size={16} weight="duotone" style={{ color: 'var(--cyan)' }} />
                            <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{bid.gc_name}</span>
                            {bid.gc_company && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({bid.gc_company})</span>}
                          </div>
                          <div className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                            <span style={{ color: 'var(--gold)' }}>${bid.amount?.toLocaleString()}</span> &middot; {bid.estimated_duration}
                          </div>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{bid.project_title}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button data-testid={`restore-bid-${bid.id.slice(0,8)}`} onClick={() => handleRestoreBid(bid.id)} size="sm" className="text-xs gap-1" style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.25)' }}>
                            Restore
                          </Button>
                          <Button data-testid={`perm-delete-bid-${bid.id.slice(0,8)}`} onClick={() => handlePermanentDeleteBid(bid.id)} size="sm" className="text-xs gap-1" style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', border: '1px solid rgba(239,68,68,0.15)' }}>
                            <Trash size={14} weight="bold" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {trashedProjects.length === 0 && trashedBids.length === 0 && (
              <div className="hb-empty p-12 text-center">
                <Trash size={56} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                <h3 className="text-lg font-semibold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>Trash is empty</h3>
                <p style={{ color: 'var(--text-secondary)' }}>Deleted projects and bids will appear here.</p>
              </div>
            )}
          </div>
        ) : activeProperty ? (() => {
          const prop = properties.find(p => p.id === activeProperty);
          if (!prop) return null;
          const propBids = ownerBids.filter(b => (prop.bid_ids || []).includes(b.id));
          return (
            <div data-testid="property-view">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <House size={24} weight="duotone" style={{ color: 'var(--gold)' }} />
                  <div>
                    <h2 className="text-2xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>{prop.name}</h2>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{propBids.length} bid{propBids.length !== 1 ? 's' : ''} assigned</p>
                  </div>
                </div>
                <Button onClick={() => setActiveProperty(null)} size="sm" className="btn-ghost text-xs" style={{ border: '1px solid var(--border-secondary)' }}>
                  <X size={14} className="mr-1" /> Close
                </Button>
              </div>
              {propBids.length > 0 ? (
                <div className="space-y-3">
                  {propBids.map(bid => (
                    <div key={bid.id} data-testid={`prop-bid-${bid.id.slice(0,8)}`} className="hb-card p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <HardHat size={16} weight="duotone" style={{ color: 'var(--cyan)' }} />
                            <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{bid.gc_name}</span>
                            {bid.gc_company && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({bid.gc_company})</span>}
                            <span className={`hb-badge hb-badge-${bid.status}`}>{bid.status.replace('_', ' ')}</span>
                          </div>
                          <div className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                            <span style={{ color: 'var(--gold)' }}>${bid.amount?.toLocaleString()}</span> &middot; {bid.estimated_duration}
                          </div>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{bid.project_title}</p>
                          {bid.proposal && <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{bid.proposal}</p>}
                        </div>
                        <button data-testid={`remove-bid-from-prop-${bid.id.slice(0,8)}`}
                          onClick={async () => { try { await axios.delete(`${API}/properties/${prop.id}/bids/${bid.id}`); toast.success('Bid removed'); fetchProperties(); } catch { toast.error('Failed'); } }}
                          className="p-1.5 rounded-lg shrink-0" style={{ color: 'var(--text-muted)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                          title="Remove from property">
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="hb-empty p-12 text-center">
                  <House size={56} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <h3 className="text-lg font-semibold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>No bids assigned yet</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>Drag bids from Bid Responses into this property to organize them.</p>
                </div>
              )}
            </div>
          );
        })() : (
        <>
        {/* ═══ BID RESPONSES Section ═══ */}
        <div className="mb-8" data-testid="bid-responses-section">
          <div className="flex items-center gap-3 mb-4">
            <Gavel size={22} weight="duotone" style={{ color: 'var(--gold)' }} />
            <h2 className="text-xl font-bold font-heading uppercase tracking-wide" style={{ color: 'var(--gold-light)' }}>Bid Responses</h2>
          </div>
          <BidManagement userId={user.id} onProjectDragStart={(projectId) => {
            const proj = projects.find(p => p.id === projectId);
            if (proj) setDraggedProject(proj);
          }} onDragEnd={() => setDraggedProject(null)} onBidTrashed={() => { fetchOwnerBids(); fetchTrashedBids(); }} />
        </div>

        {/* Chrome divider */}
        <div className="hb-gold-divider mb-8" />

        {/* Projects Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>
            {SIDEBAR_FILTERS.find(f => f.id === activeFilter)?.label || 'My Projects'}
          </h2>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="create-project-button" className="btn-gold gap-2">
                    <Plus size={20} weight="bold" /> Post New Project
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}>
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading" style={{ color: 'var(--gold-light)' }}>Post a New Project</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                    <div>
                      <Label style={{ color: 'var(--text-secondary)' }}>Project Title</Label>
                      <Input data-testid="project-title-input" value={newProject.title} onChange={(e) => setNewProject({ ...newProject, title: e.target.value })} placeholder="e.g., Roof Replacement - Oak Apartments" required className="hb-input" />
                    </div>
                    <div>
                      <Label style={{ color: 'var(--text-secondary)' }}>Location</Label>
                      <Input data-testid="project-location-input" value={newProject.location} onChange={(e) => setNewProject({ ...newProject, location: e.target.value })} placeholder="Dallas, TX" required className="hb-input" />
                    </div>
                    <div>
                      <Label style={{ color: 'var(--text-secondary)' }}>Budget Range</Label>
                      <Input data-testid="project-budget-input" value={newProject.budget_range} onChange={(e) => setNewProject({ ...newProject, budget_range: e.target.value })} placeholder="$50,000 - $75,000" required className="hb-input" />
                    </div>
                    <div>
                      <Label style={{ color: 'var(--text-secondary)' }}>Project Deadline</Label>
                      <Input data-testid="project-deadline-input" type="date" value={newProject.deadline} onChange={(e) => setNewProject({ ...newProject, deadline: e.target.value })} required className="hb-input" />
                    </div>
                    <div>
                      <Label style={{ color: 'var(--text-secondary)' }}>Description</Label>
                      <Textarea data-testid="project-description-input" value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} placeholder="Provide a detailed description of the project..." rows={4} required className="hb-input" />
                    </div>
                    <div>
                      <Label style={{ color: 'var(--text-secondary)' }}>Project Specifications</Label>
                      <Textarea data-testid="project-specs-input" value={newProject.specs} onChange={(e) => setNewProject({ ...newProject, specs: e.target.value })} placeholder="List specific requirements, materials, codes, etc." rows={4} required className="hb-input" />
                    </div>
                    <div>
                      <Label style={{ color: 'var(--text-secondary)' }}>Project Photos (Optional)</Label>
                      <div {...getRootProps()} className={`hb-upload-zone rounded-lg p-8 text-center cursor-pointer ${isDragActive ? 'active' : ''}`}>
                        <input {...getInputProps()} />
                        <ImageIcon size={32} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {isDragActive ? 'Drop photos here...' : 'Drag & drop photos, or click to select'}
                        </p>
                      </div>
                      {projectPhotos.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {projectPhotos.map((file, idx) => (
                            <div key={idx} className="text-xs px-3 py-1 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>{file.name}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label style={{ color: 'var(--text-secondary)' }}>Project Documents (PDF only)</Label>
                      <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Upload scope of work, floor plans, specs, or other project PDFs</p>
                      <label className="hb-upload-zone rounded-lg p-6 text-center cursor-pointer block" data-testid="project-docs-upload-zone">
                        <input
                          data-testid="project-docs-input"
                          type="file"
                          accept=".pdf"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            const pdfs = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
                            if (pdfs.length < files.length) toast.error('Only PDF files are allowed');
                            setProjectDocs(prev => [...prev, ...pdfs]);
                            e.target.value = '';
                          }}
                        />
                        <FileText size={32} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Click to select PDF files</p>
                      </label>
                      {projectDocs.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {projectDocs.map((file, idx) => (
                            <div key={idx} data-testid={`project-doc-${idx}`} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                              <div className="flex items-center gap-2">
                                <FileText size={16} weight="duotone" style={{ color: 'var(--gold)' }} />
                                <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{file.name}</span>
                                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({(file.size / 1024).toFixed(0)} KB)</span>
                              </div>
                              <button type="button" data-testid={`remove-doc-${idx}`} onClick={() => setProjectDocs(prev => prev.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-300 p-1">
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1 btn-ghost" style={{ border: '1px solid var(--border-secondary)' }}>Cancel</Button>
                      <Button type="submit" data-testid="submit-project-button" className="flex-1 btn-gold" disabled={creatingProject}>
                        {creatingProject ? 'Creating...' : 'Post Project'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  data-testid={`project-card-${project.id}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, project)}
                  onDragEnd={() => setDraggedProject(null)}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="hb-card overflow-hidden hb-lift cursor-pointer"
                  style={{ opacity: draggedProject?.id === project.id ? 0.5 : 1 }}
                >
                  {project.photos.length > 0 ? (
                    <div className="h-48 overflow-hidden" style={{ background: 'var(--bg-elevated)' }}>
                      <AuthImage fileRef={project.photos[0]} alt={project.title} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center" style={{ background: 'var(--bg-elevated)' }}>
                      <Buildings size={48} weight="duotone" style={{ color: 'var(--text-muted)' }} />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-lg font-semibold font-heading mb-3 line-clamp-2" style={{ color: 'var(--text-primary)' }}>{project.title}</h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><MapPin size={16} weight="duotone" /> {project.location}</div>
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><CurrencyDollar size={16} weight="duotone" /> {project.budget_range}</div>
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><CalendarBlank size={16} weight="duotone" /> Deadline: {new Date(project.deadline).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={getStatusColor(project.status)}>{project.status.replace('_', ' ')}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>{project.bid_count} {project.bid_count === 1 ? 'bid' : 'bids'}</span>
                        <button
                          data-testid={`delete-project-${project.id}`}
                          onClick={(e) => handleTrashProject(e, project.id, project.title)}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ color: 'var(--text-muted)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
                          title="Move to Trash"
                        >
                          <Trash size={18} weight="duotone" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredProjects.length === 0 && (
              <div className="hb-empty p-12 text-center">
                <Buildings size={64} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                <h3 className="text-xl font-semibold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>
                  No {SIDEBAR_FILTERS.find(f => f.id === activeFilter)?.label.toLowerCase() || 'projects'} yet
                </h3>
                <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {activeFilter === 'new' ? 'Get started by posting your first project' : activeFilter === 'denied' ? 'Projects where all bids have been denied will appear here' : 'Projects with this status will appear here'}
                </p>
                {activeFilter === 'new' && (
                  <Button onClick={() => setShowCreateDialog(true)} className="btn-gold gap-2">
                    <Plus size={20} weight="bold" /> Post New Project
                  </Button>
                )}
              </div>
            )}
        </>
        )}
      </main>
      </div>
    </div></div>
  );
};

export default OwnerDashboard;
